/* ==========================================================================
   as-back-in-stock.js
   <as-bis> — the waitlist on a sold-out product.

   THREE STEPS, FROM THE MOCKUP.

     idle   Join the waitlist            (cards only; the PDP opens on email)
     email  address, required
     sms    mobile, optional, skippable
     done   confirmation

   WHAT IT SENDS, AND WHY IT IS MORE THAN ONE CALL.

   1. A back-in-stock subscription for the VARIANT, to
      /client/back-in-stock-subscriptions/. This is the one that matters: it
      is what Klaviyo's own button creates, so it feeds the existing Back in
      Stock flow and nothing in Klaviyo has to be rebuilt. It is per variant,
      so only the people waiting on the length that came back are emailed.

   2. A list subscription, to /client/subscriptions/. The back-in-stock call
      takes no list — it is a notification, not marketing consent — so this
      second call is what puts every waitlist sign-up in one place, which is
      what was asked for. Skipped when no list is configured.

   3. On the optional SMS step, the same two again with the phone number and
      SMS consent. The email address rides along as the identifier, or
      Klaviyo creates a second profile instead of merging.

   PUBLIC KEY ONLY. These are the client endpoints; a private key must never
   reach a browser.

   THE JOINED STATE IS REMEMBERED LOCALLY, AND CANNOT BE ANYTHING ELSE.
   Klaviyo has no client-side way to ask "is this person already waiting on
   this variant", so "On your waitlist" comes from localStorage: right on the
   same browser, absent on another. That is a known limit of the design, not
   a defect — the subscription itself is server-side and correct either way.
   ========================================================================== */

(function () {
  'use strict';

  var BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var LIST_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var EVENT_ENDPOINT = 'https://a.klaviyo.com/client/events/';
  var STORE_KEY = 'as-bis-joined';

  /* Shopify catalogue ids in Klaviyo are namespaced. The integration writes
     every variant under this shape; a bare variant id matches nothing and the
     call is accepted but never fires. */
  function catalogId(variantId) {
    return '$shopify:::$default:::' + variantId;
  }

  /* --------------------------------------------------------------------------
     DIALLING CODES COME FROM as-dial-codes.js.

     The list used to live here. It is shared with the BF26 early access page
     now, and two copies of 212 countries drift the moment one is edited.

     If that file has not loaded the picker is simply left alone: the field
     still accepts a number written in full with a +, which the submit path
     already handles, so a missing asset costs the convenience and not the
     sign-up.
     -------------------------------------------------------------------------- */

  function joinedSet() {
    try {
      return JSON.parse(window.localStorage.getItem(STORE_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function remember(variantId) {
    try {
      var all = joinedSet();
      if (all.indexOf(String(variantId)) === -1) {
        all.push(String(variantId));
        window.localStorage.setItem(STORE_KEY, JSON.stringify(all));
      }
    } catch (e) {
      /* Private mode, or storage full. The subscription still happened. */
    }
  }

  function emailLooksValid(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  class AsBis extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bisInit === '1') return;
      this.dataset.bisInit = '1';

      this.publicKey = this.dataset.publicKey || '';
      this.listId = this.dataset.listId || '';
      /* Phone sign-ups land here instead of the email list. Falls back to the
         email list when unset, which is how this behaved before the SMS
         audience existed. */
      this.smsListId = this.dataset.smsListId || this.dataset.listId || '';
      this.revision = this.dataset.revision || '2026-07-15';
      this.source = this.dataset.source || 'Back in stock';
      this.variantId = this.dataset.variantId || '';
      this.metric = this.dataset.metric || '';
      this.smsEnabled = this.dataset.sms === 'true';

      this.email = '';
      this.phone = '';

      this._bind();

      /* Returning to a product already joined on this browser. */
      if (this.variantId && joinedSet().indexOf(String(this.variantId)) !== -1) {
        this._fillDone();
        this._go('done', true);
      }
    }

    _bind() {
      var self = this;

      var open = this.querySelector('[data-bis-open]');
      if (open) {
        open.addEventListener('click', function () {
          self._go('email');
        });
      }

      /* The two steps are divs, not forms — the panel sits inside Shopify's
         product form and HTML will not nest one form in another. So the
         button is wired directly, and Enter in the field is wired to match
         what a form would have done. */
      this._wire('[data-bis-email-form]', function () {
        self._submitEmail();
      });

      this._wire('[data-bis-sms-form]', function () {
        self._submitSms();
      });

      var skip = this.querySelector('[data-bis-skip]');
      if (skip) {
        skip.addEventListener('click', function () {
          self._finish();
        });
      }
    }

    _wire(selector, run) {
      var row = this.querySelector(selector);
      if (!row) return;

      var button = row.querySelector('[data-bis-submit]');
      if (button) button.addEventListener('click', run);

      var field = row.querySelector('input');
      if (field) {
        field.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          run();
        });
      }
    }


    /* ---------------------------------------------------------------- view */

    /*
      FILL THE CODE LIST, ONCE, ON ARRIVAL AT STEP 2.

      Deferred rather than done at init: most tiles never reach this step, and
      building 200 options for each of them would be paid for on every grid.
    */
    _fillDial() {
      var select = this.querySelector('[data-bis-dial]');
      if (!select || !window.AsDialCodes) return;

      /* Deferred until step 2: most tiles never reach it, and building 212
         options for each of them would be paid for on every grid. */
      window.AsDialCodes.fill(select, select.dataset.defaultIso);
    }

    _go(step, silent) {
      if (step === 'sms') this._fillDial();

      this.querySelectorAll('[data-bis-step]').forEach(function (panel) {
        panel.hidden = panel.dataset.bisStep !== step;
      });

      if (silent) return;

      /* Focus the field the customer is now looking at, but never on the
         first paint — stealing focus on load scrolls the page. */
      var field = this.querySelector('[data-bis-step="' + step + '"] input');
      if (field) field.focus();
    }

    _error(message) {
      var slot = this.querySelector('[data-bis-error]');
      if (!slot) return;
      slot.textContent = message || '';
      slot.hidden = !message;
    }

    _busy(on) {
      this.querySelectorAll('[data-bis-submit]').forEach(function (btn) {
        btn.disabled = on;
        btn.setAttribute('aria-busy', on ? 'true' : 'false');
      });
    }

    /*
      FILL THE CONFIRMATION COPY.

      Separate from _finish because the done state is reached two ways: by
      joining now, and by arriving on a product this browser joined earlier.
      Only the first ran this, so a returning customer read "we'll tell you
      first when is back" — the {variant} slot left empty.
    */
    _fillDone() {
      /* The label Liquid already resolved — the same string the heading uses. */
      var label = this.dataset.variantLabel || '';
      this.querySelectorAll('[data-bis-variant]').forEach(function (slot) {
        slot.textContent = label;
      });

      /* The "and text" clause only belongs there if she actually gave one. */
      this.querySelectorAll('[data-bis-if-phone]').forEach(function (slot) {
        slot.hidden = !this.phone;
      }, this);
    }

    _finish() {
      remember(this.variantId);
      this._fillDone();
      this._go('done');
    }

    /* ---------------------------------------------------------------- send */

    _post(url, body) {
      if (!this.publicKey) {
        console.error(
          '[as-back-in-stock] No Klaviyo public key set — nothing was sent. ' +
            'Theme settings → Back in stock → Klaviyo public key.'
        );
        return Promise.resolve({ ok: false, status: 0 });
      }

      return fetch(url + '?company_id=' + encodeURIComponent(this.publicKey), {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          revision: this.revision
        },
        body: JSON.stringify(body)
      });
    }

    _bisBody(channels, withPhone) {
      var attributes = { email: this.email };
      if (withPhone && this.phone) attributes.phone_number = this.phone;

      return {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: channels,
            profile: { data: { type: 'profile', attributes: attributes } }
          },
          relationships: {
            variant: {
              data: { type: 'catalog-variant', id: catalogId(this.variantId) }
            }
          }
        }
      };
    }

    _listBody(subscriptions, withPhone, withSource, listId) {
      var attributes = { email: this.email, subscriptions: subscriptions };
      if (withPhone && this.phone) attributes.phone_number = this.phone;

      var body = {
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: attributes } }
          }
        }
      };

      /* custom_source becomes $source on the profile. Only the first call
         sends it: the SMS step runs second, and stamping it again would
         overwrite where the sign-up actually came from. */
      if (withSource) body.data.attributes.custom_source = this.source;

      var target = listId || this.listId;
      if (target) {
        body.data.relationships = { list: { data: { type: 'list', id: target } } };
      }

      return body;
    }

    /*
      ONE EVENT PER JOIN — THIS IS WHAT THE CONFIRMATION EMAIL TRIGGERS ON.

      Not list membership. "Added to list" fires the first time someone joins
      and never again, because the second join adds them to a list they are
      already on — so a customer waiting on three products would be confirmed
      once and left wondering about the other two.

      An event fires every time, and carries the product with it, so the
      confirmation can name what she actually joined.
    */
    _fireEvent() {
      if (!this.metric) return Promise.resolve();

      return this._post(EVENT_ENDPOINT, {
        data: {
          type: 'event',
          attributes: {
            properties: {
              ProductName: this.dataset.productTitle || '',
              /* Blank on a product with no real options — see the snippet.
                 The email tests this to decide whether to name a length. */
              VariantName: this.dataset.variantName || '',
              VariantID: this.variantId,
              URL: this.dataset.productUrl || '',
              ImageURL: this.dataset.productImage || ''
            },
            metric: {
              data: { type: 'metric', attributes: { name: this.metric } }
            },
            profile: {
              data: { type: 'profile', attributes: { email: this.email } }
            }
          }
        }
      });
    }

    _submitEmail() {
      var input = this.querySelector('[data-bis-email]');
      var value = input ? input.value.trim() : '';

      if (!emailLooksValid(value)) {
        this._error(this.dataset.invalidEmail || 'Enter a valid email address.');
        if (input) input.focus();
        return;
      }

      this._error('');
      this._busy(true);
      this.email = value;

      var self = this;
      var calls = [this._post(BIS_ENDPOINT, this._bisBody(['EMAIL'], false))];

      calls.push(this._fireEvent());

      if (this.listId) {
        calls.push(
          this._post(
            LIST_ENDPOINT,
            this._listBody({ email: { marketing: { consent: 'SUBSCRIBED' } } }, false, true, this.listId)
          )
        );
      }

      /* allSettled, not all: the list call is a nice-to-have and must never
         cost her the back-in-stock subscription, which is the one that
         actually gets her told. */
      Promise.allSettled(calls).then(function (results) {
        self._busy(false);

        var bis = results[0];
        var ok = bis.status === 'fulfilled' && (bis.value.ok || bis.value.status === 202);

        if (!ok) {
          self._error(self.dataset.failMessage || 'Something went wrong. Try again.');
          return;
        }

        if (self.smsEnabled) {
          self._go('sms');
        } else {
          self._finish();
        }
      });
    }

    _submitSms() {
      var input = this.querySelector('[data-bis-phone]');
      var value = input ? input.value.trim() : '';

      /* Empty is a valid answer on an optional step — treat it as the skip. */
      if (!value) {
        this._finish();
        return;
      }

      /*
        ASSEMBLE E.164 FROM THE PICKER AND WHAT SHE TYPED.

        A number already written in full wins — someone who types +44 7… means
        it, and prefixing the selector on top would produce nonsense. Otherwise
        the chosen code is prepended.

        The leading zero goes. Most of the world writes its own numbers with a
        trunk prefix — 07700 900123 — which is domestic notation only and is
        not part of the international number.
      */
      var typed = value.replace(/[\s()–—-]/g, '');
      var full;

      if (typed.charAt(0) === '+') {
        full = typed;
      } else {
        var select = this.querySelector('[data-bis-dial]');
        var dial = select ? select.value : '';
        full = dial + typed.replace(/^0+/, '');
      }

      /* Klaviyo wants E.164 and silently drops anything else. Rejecting it
         here is the difference between a visible retry and a number that
         vanishes without trace. */
      if (!/^\+[1-9]\d{6,14}$/.test(full)) {
        this._error(this.dataset.invalidPhone || 'Enter your number in full, starting with the country code.');
        if (input) input.focus();
        return;
      }

      this._error('');
      this._busy(true);
      this.phone = full;

      var self = this;
      var calls = [this._post(BIS_ENDPOINT, this._bisBody(['EMAIL', 'SMS'], true))];

      /*
        THE NUMBER GOES TO THE SMS LIST, NOT THE EMAIL ONE.

        She is already on the email list from step one, so this call exists to
        put her somewhere the team can text — a separate audience that can be
        messaged on its own without reaching everyone who only gave an email.

        SMS consent is only ever sent here, and only when she typed a number:
        the consent line above the field is what she agreed to.
      */
      if (this.smsListId) {
        calls.push(
          this._post(
            LIST_ENDPOINT,
            this._listBody(
              { sms: { marketing: { consent: 'SUBSCRIBED' } } },
              true,
              false,
              this.smsListId
            )
          )
        );
      }

      Promise.allSettled(calls).then(function () {
        self._busy(false);
        /* Whatever happened to the SMS consent, she is already on the email
           waitlist from step one. Sending her back to a form she has
           completed would be worse than a text that did not register. */
        self._finish();
      });
    }
  }

  if (!customElements.get('as-bis')) {
    customElements.define('as-bis', AsBis);
  }

  /* --------------------------------------------------------------------------
     THE BADGE ON A TILE SHE HAS ALREADY JOINED.

     Not part of <as-bis>: the badge sits above the product name, outside the
     widget, so it is swept separately. Server-rendered as "Sold out · Waitlist
     open" and rewritten here, because the page is cached and cannot know who
     is looking.

     Run again after AJAX pagination and after a variant swap, which replace
     tiles that were never touched on first paint.
     -------------------------------------------------------------------------- */
  function markJoinedBadges(root) {
    var joined = joinedSet();
    if (!joined.length) return;

    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-bis-badge]').forEach(function (badge) {
      if (badge.dataset.bisJoined === '1') return;
      if (joined.indexOf(String(badge.dataset.variantId)) === -1) return;

      var label = badge.dataset.joinedLabel;
      if (!label) return;

      badge.dataset.bisJoined = '1';
      badge.textContent = label;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      markJoinedBadges();
    });
  } else {
    markJoinedBadges();
  }

  /* Dawn fires this after the Section Rendering API swaps markup in. */
  document.addEventListener('shopify:section:load', function (event) {
    markJoinedBadges(event.target);
  });
  document.addEventListener('as:collection:updated', function () {
    markJoinedBadges();
  });
  document.addEventListener('collection:ajax-paginated', function () {
    markJoinedBadges();
  });

  /*
    OBSERVE THE CONTAINER, NOT THE GRID — the same reasoning as
    as-collection-tile-video.js, and deliberately the same target.

    Filtering and AJAX pagination both assign ProductGridContainer.innerHTML,
    which throws #product-grid away. An observer on the grid would be watching
    a discarded node. The container survives every swap, so one observer
    covers both, and nothing outside the grid is watched.
  */
  if (window.MutationObserver) {
    var watch = function () {
      var el =
        document.getElementById('ProductGridContainer') ||
        document.getElementById('product-grid');
      if (!el) return;

      /* One innerHTML assignment emits a burst of records; collapse it. */
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          markJoinedBadges(el);
        });
      }).observe(el, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', watch);
    } else {
      watch();
    }
  }
})();
